<template>
	<div class="label-share-donut">
		<div class="analysis-header">
			<div class="selector">
				<div class="dropdown" :class="{ 'is-active': isDropdownOpen }">
					<div class="dropdown-trigger" @click="toggleDropdown" aria-haspopup="true" aria-controls="dropdown-menu">
						<span>{{ dropdownLabel }}</span>
					</div>
					<div class="dropdown-menu" id="dropdown-menu" role="menu">
						<div class="dropdown-content">
							<p>{{ maxSelectLabel }}</p>
							<div class="dropdown-item">
								<label class="checkbox" v-for="item in availableItems" :key="item.id">
									<CustomCheckBox
										type="checkbox"
										:name="`label-${item.id}`"
										:checked="selectedItemIds.includes(item.id)"
										@change="e => handleItemSelection(e, item.id)"
										:rounded="false"
										:disabled="selectedItemIds.length >= props.maxSelections && !selectedItemIds.includes(item.id)"
									/>
									{{ item.name }}
								</label>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

	<div class="donut-grid" v-if="selectedItemsData.some(item => hasRenderableData(item))">
		<div class="donut-card" v-for="item in selectedItemsData.filter(item => hasRenderableData(item))" :key="item.id">
				<div class="chart-header">
					<h4 class="chart-title">{{ item.name }}</h4>
				</div>

				<div v-if="hasRenderableData(item)" class="chart-content">
					<div class="donut-wrapper">
						<apexchart type="donut" height="300" :options="getChartOptions(item)" :series="getChartSeries(item)" />
					</div>
					<div class="legend-list">
						<div v-for="(segment, index) in getSegments(item)" :key="`${item.id}-${segment.key}`" :class="['legend-item', { 'is-no-delta': !isRangeComparison }]">
							<span class="legend-dot" :style="{ backgroundColor: getSegmentColor(segment, index) }"></span>
							<span class="legend-name">{{ segment.name }}</span>
							<span class="legend-share">{{ formatPercent(segment.share) }}</span>
							<span v-if="isRangeComparison" :class="['legend-diff', getDeltaClass(getSegmentDelta(item, segment))]">
								{{ formatDeltaPercent(getSegmentDelta(item, segment)) }}
							</span>
						</div>
					</div>
				</div>
				<!-- <div v-else class="empty-mini">{{ t('no_data') }}</div> -->
			</div>
		</div>

		<div class="empty-state" v-else>
			{{ emptyStateMessage }}
		</div>
	</div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useDashboardGeoStore } from '@/store/dashboard/dashboardGeo.js';
import { useSubNavigationStore } from '@/store/global/subNavigation';
import { useI18n } from 'vue-i18n';
import CustomCheckBox from '@/components/utilities/customCheckBox.vue';

const { t } = useI18n();

const dashboardStore = useDashboardGeoStore();
const { analysis, last_analysis, selectedDate } = storeToRefs(dashboardStore);

const subNavigationStore = useSubNavigationStore();
const { selectedAiTypeDashboard } = storeToRefs(subNavigationStore);

const props = defineProps({
	analysisType: {
		type: String,
		default: 'labels',
	},
	maxSelections: {
		type: Number,
		default: 6,
	},
});

const isDropdownOpen = ref(false);
const selectedItemIds = ref([]);

const palette = ['#0EA5A1', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#14B8A6', '#3B82F6'];
const clientColor = '#1D4ED8';
const otherColor = '#B0B5C3';

const currentLabelsDonut = computed(() => {
	const aiType = selectedAiTypeDashboard.value;
	return analysis.value?.[aiType]?.labels_donut || [];
});

const previousLabelsDonutById = computed(() => {
	const aiType = selectedAiTypeDashboard.value;
	const rows = analysis.value?.[aiType]?.labels_donut ? last_analysis.value?.[aiType]?.labels_donut || [] : [];
	const byId = new Map();
	rows.forEach(item => {
		byId.set(Number(item?.id), item);
	});
	return byId;
});

const isRangeComparison = computed(() => {
	const range = selectedDate.value || [];
	if (range.length < 2 || !range[0] || !range[1]) {
		return false;
	}
	return new Date(range[0]).getTime() !== new Date(range[1]).getTime();
});

const availableItems = computed(() => {
	return currentLabelsDonut.value
		.filter(item => hasRenderableData(item))
		.map(item => ({
			id: Number(item?.id),
			name: String(item?.name || ''),
			isProductService: item?.is_product_service === true,
		}))
		.sort((a, b) => String(a.name).localeCompare(String(b.name)));
});

const selectedItemsData = computed(() => {
	const byId = new Map(currentLabelsDonut.value.map(item => [item.id, item]));
	return selectedItemIds.value.map(id => byId.get(id)).filter(Boolean);
});

const dropdownLabel = computed(() => t('select_labels'));
const maxSelectLabel = computed(() => `Maximum ${props.maxSelections} labels`);
const emptyStateMessage = computed(() => {
	if (availableItems.value.length === 0) {
		return 'No question labels available for the selected period.';
	}
	return 'Select labels to display the charts.';
});

const toggleDropdown = () => {
	isDropdownOpen.value = !isDropdownOpen.value;
};

const closeDropdown = event => {
	if (!event.target.closest('.dropdown')) {
		isDropdownOpen.value = false;
	}
};

const handleItemSelection = (event, itemId) => {
	if (event.target.checked) {
		if (selectedItemIds.value.length < props.maxSelections && !selectedItemIds.value.includes(itemId)) {
			selectedItemIds.value = [...selectedItemIds.value, itemId];
		}
	} else {
		selectedItemIds.value = selectedItemIds.value.filter(id => id !== itemId);
	}
};

const getDefaultSelection = items => {
	const prioritized = [
		...items.filter(item => item.isProductService),
		...items.filter(item => !item.isProductService),
	];
	return prioritized.slice(0, props.maxSelections).map(item => item.id);
};

const capitalizeName = value => {
	const raw = String(value || '').trim();
	if (!raw) {
		return '';
	}

	const cleaned = raw
		.replace(/^https?:\/\//i, '')
		.replace(/^www\./i, '')
		.split('/')[0]
		.split('?')[0]
		.split('#')[0]
		.split(':')[0]
		.trim();

	let baseName = cleaned;
	if (cleaned && !cleaned.includes(' ') && cleaned.includes('.')) {
		const parts = cleaned
			.toLowerCase()
			.split('.')
			.map(part => part.trim())
			.filter(Boolean);
		const rootLikeParts = new Set(['co', 'com', 'net', 'org', 'gov', 'edu']);

		if (parts.length >= 3 && parts[parts.length - 1].length === 2 && rootLikeParts.has(parts[parts.length - 2])) {
			baseName = parts[parts.length - 3];
		} else if (parts.length >= 2) {
			baseName = parts[parts.length - 2];
		} else {
			baseName = parts[0] || cleaned;
		}
	}

	return String(baseName || raw)
		.split(' ')
		.map(word =>
			word
				.split('-')
				.map(part => {
					if (!part) return part;
					return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
				})
				.join('-'),
		)
		.join(' ');
};

const getSegments = item => {
	const rawSegments = Array.isArray(item?.segments) ? item.segments : [];
	const segments = rawSegments.map(segment => ({
		...segment,
		mention_count: Number(segment?.mention_count || 0),
		share: Number(segment?.share || 0),
		name: segment?.is_other ? 'Other' : capitalizeName(segment?.name),
	}));

	const nonOther = segments
		.filter(segment => !segment.is_other)
		.sort((a, b) => {
			if (b.mention_count !== a.mention_count) {
				return b.mention_count - a.mention_count;
			}
			return String(a.name).localeCompare(String(b.name));
		});

	const other = segments.find(segment => segment.is_other);
	return other ? [...nonOther, other] : nonOther;
};

const getSegmentColor = (segment, index) => {
	if (segment?.is_other) {
		return otherColor;
	}
	if (segment?.is_client) {
		return clientColor;
	}
	return palette[index % palette.length];
};

const getChartSeries = item => {
	return getSegments(item).map(segment => Number(segment.mention_count || 0));
};

const hasRenderableData = item => {
	const total = getChartSeries(item).reduce((sum, value) => sum + value, 0);
	return total > 0;
};

const formatPercent = share => `${(Number(share || 0) * 100).toFixed(1)}%`;
const normalizedDeltaPercent = deltaShare => Number((Number(deltaShare || 0) * 100).toFixed(1));
const formatDeltaPercent = deltaShare => {
	const value = normalizedDeltaPercent(deltaShare);
	const fixed = Math.abs(value).toFixed(1);
	if (value > 0) return `+${fixed}%`;
	if (value < 0) return `-${fixed}%`;
	return '0.0%';
};
const getDeltaClass = deltaShare => {
	const value = normalizedDeltaPercent(deltaShare);
	if (value > 0) return 'is-positive';
	if (value < 0) return 'is-negative';
	return 'is-neutral';
};
const getSegmentDelta = (item, segment) => {
	const prevItem = previousLabelsDonutById.value.get(Number(item?.id));
	const prevSegments = Array.isArray(prevItem?.segments) ? prevItem.segments : [];
	const prevMap = new Map(prevSegments.map(row => [String(row?.key ?? ''), Number(row?.share || 0)]));
	const current = Number(segment?.share || 0);
	const previous = Number(prevMap.get(String(segment?.key ?? '')) || 0);
	return current - previous;
};

const getChartOptions = item => {
	const segments = getSegments(item);
	const colors = segments.map((segment, index) => getSegmentColor(segment, index));

	return {
		chart: {
			type: 'donut',
			toolbar: {
				show: false,
			},
		},
		labels: segments.map(segment => segment.name),
		colors,
		stroke: {
			width: 2,
			colors: ['#fff'],
		},
		dataLabels: {
			enabled: true,
			formatter: value => `${Number(value || 0).toFixed(1)}%`,
			style: {
				fontSize: '13px',
				fontWeight: 700,
			},
			dropShadow: {
				enabled: false,
			},
		},
		legend: {
			show: false,
		},
		tooltip: {
			y: {
				formatter: (value, { seriesIndex }) => {
					const segment = segments[seriesIndex] || {};
					return `${formatPercent(segment.share)} (${segment.mention_count || 0} mentions)`;
				},
			},
		},
		plotOptions: {
			pie: {
				donut: {
					size: '62%',
				},
			},
		},
	};
};

watch(
	availableItems,
	items => {
		const validIds = new Set(items.map(item => item.id));
		const cleaned = selectedItemIds.value.filter(id => validIds.has(id));
		if (cleaned.length !== selectedItemIds.value.length) {
			selectedItemIds.value = cleaned;
		}

		if (selectedItemIds.value.length === 0 && items.length > 0) {
			selectedItemIds.value = getDefaultSelection(items);
		}
	},
	{ immediate: true },
);

onMounted(() => {
	document.addEventListener('click', closeDropdown);
});

onUnmounted(() => {
	document.removeEventListener('click', closeDropdown);
});
</script>

<style lang="scss" scoped>
.label-share-donut {
	padding: 0.6rem;

	.analysis-header {
		display: flex;
		justify-content: flex-end;
	}

	.selector {
		.dropdown {
			position: relative;

			.dropdown-trigger {
				display: flex;
				align-items: center;
				padding: 0.8rem 1.2rem;
				border: 2px solid var(--gris-100);
				border-radius: 8px;
				background: var(--blanc);
				cursor: pointer;
				font-size: 0.9rem;
			}

			&.is-active {
				.dropdown-trigger {
					border-color: var(--bleu-200);
				}
			}

			.dropdown-menu {
				position: absolute;
				top: 100%;
				right: 0;
				z-index: 1000;
				min-width: 280px;
				margin-top: 4px;
				opacity: 0;
				visibility: hidden;
				transform: translateY(-8px);
				transition: all 0.2s;

				.dropdown-content {
					background: var(--blanc);
					border: 1px solid var(--gris-100);
					border-radius: 8px;
					max-height: 360px;
					overflow: auto;
					padding: 0.4rem 0.8rem;

					p {
						margin: 0.2rem 0 0.5rem 0;
						font-size: 0.85rem;
						color: var(--gris-500);
					}

					.checkbox {
						display: flex;
						align-items: center;
						gap: 0.6rem;
						font-size: 0.9rem;
						padding: 0.2rem 0;
					}
				}
			}

			&.is-active .dropdown-menu {
				opacity: 1;
				visibility: visible;
				transform: translateY(0);
			}
		}
	}

	.donut-grid {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 1.5rem;
		margin-top: 1rem;
	}

	.donut-card {
		padding: .8rem;
	}

	.chart-header {
		margin-bottom: 0.5rem;
		text-align: center;
		font-size: 1.05rem;
	}
	
	.chart-content {
		display: flex;
		gap: 0.8rem;
		align-items: center;
	}
	
	.donut-wrapper {
		flex: 0 0 55%;
	}
	
	.legend-list {
		flex: 1;
		max-height: 260px;
		overflow: auto;
		overflow-x: hidden;
		padding: 1rem;
		display: grid;
		grid-template-columns: auto minmax(0, min(170px)) auto auto;
		gap: 0.25rem 0.75rem;
		align-content: start;
		min-width: 0;
	}
	
	.legend-item {
		display: grid;
		grid-column: 1 / -1;
		grid-template-columns: subgrid;
		align-items: center;
		font-size: 0.9rem;
		min-width: 0;
	}
	
	.legend-dot {
		width: 10px;
		height: 10px;
		border-radius: 50%;
		justify-self: start;
	}
	
	.legend-name {
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		color: var(--gris-700);
		padding: 0.2rem 0;
		margin-left: -.5rem;
	}
	
	.legend-share {
		font-weight: 600;
		color: var(--gris-900);
		text-align: right;
		white-space: nowrap;
		justify-self: end;
	}
	
	.legend-diff {
		font-weight: 600;
		text-align: right;
		white-space: nowrap;
		justify-self: end;
	}
	
	.legend-diff.is-positive {
		color: var(--vert-500);
	}
	
	.legend-diff.is-negative {
		color: var(--rouge-500);
	}

	.legend-diff.is-neutral {
		color: var(--gris-400);
	}

	.empty-mini {
		padding: 2rem 0;
		text-align: center;
		color: var(--gris-500);
		font-style: italic;
	}

	.empty-state {
		color: var(--gris-400);
		padding: 1rem 0;
		text-align: center;
		font-size: 0.95rem;
		font-style: italic;
	}
}

@media (max-width: 1200px) {
	.label-share-donut {
		.donut-grid {
			grid-template-columns: 1fr;
		}
	}
}

@media (max-width: 768px) {
	.label-share-donut {
		.chart-content {
			flex-direction: column;
			align-items: stretch;
		}

		.donut-wrapper {
			flex: auto;
		}
	}
}
</style>
